import React from 'react';
import { Button } from "../components/ui/button";
import { ArrowRight, Download } from 'lucide-react';
import { Ship, Users, Warehouse, Calendar, MapPin, Anchor } from 'lucide-react';

// Componentes
import HeroSection from '../components/sections/HeroSection';
import StatsSection from '../components/sections/StatsSection';
import TimelineSection from '../components/sections/TimelineSection';
import InvestmentChart from '../components/charts/InvestmentChart';
import CargoCharts from '../components/sections/CargoCharts';
import ArcoNorteSection from '../components/sections/ArcoNorteSection';
import FleetAndWaterwaysSection from '../components/sections/FleeAndWaterwaysSection';
import AccidentChart from '../components/charts/AccidentChart';

import aquaviario from "../assets/img/mapas/aquaviario.png";

import diagnosticoPdf from "../assets/diagnostico/diagnostivo2024.pdf";

// Dados da publicação AQUAVIÁRIA
const waterwayData = {
  title: "Diagnóstico Logístico Aquaviário",
  subtitle: "Análise e marcos do transporte aquaviário no Brasil em 2024",
  year: "2024",
  
  stats: [
    { 
      label: "Movimentação Portuária", 
      value: 1.32, 
      suffix: "Bilhões t", 
      prefix: "",
      description: "Recorde histórico de movimentação nos portos brasileiros",
      color: "text-blue-600"
    },
    { 
      label: "Investimento Público", 
      value: 511.6, 
      suffix: "Milhões",
      prefix: "R$",
      description: "Crescimento de quase 60% em relação a 2023",
      color: "text-green-600"
    },
    { 
      label: "Carga Conteinerizada", 
      value: 20, 
      suffix: "%",
      description: "Crescimento no volume em relação ao ano anterior",
      color: "text-purple-600"
    },
    { 
      label: "Desenvolvimento do Arco Norte", 
      value: 35, 
      suffix: "%",
      description: "Participação do total de exportações do país",
      color: "text-orange-600"
    }
  ],

  timeline: [
    { month: "Janeiro", title: "Sanção da nova lei de debêntures de infraestrutura", description: "Lei nº 14.801/2024 moderniza o financiamento privado para projetos em portos, embarcações e hidrovias.", icon: "📜" },
    { month: "Fevereiro", title: "Recorde de movimentação portuária em 2023", description: "ANTAQ confirma a marca histórica de 1,3 bilhão de toneladas, consolidando a força do agronegócio e mineração.", icon: "📈" },
    { month: "Março", title: "Aprovação de norma sobre armazenagem adicional", description: "Resolução ANTAQ nº 112/2024 estabelece critérios para cobrança, evitando custos indevidos e promovendo eficiência.", icon: "📦" },
    { month: "Abril", title: "Expansão do Porto de Santos com o terminal STS-10", description: "Poligonal do porto é ampliada para viabilizar novo terminal de contêineres, reforçando sua liderança.", icon: "🏗️" },
    { month: "Maio", title: "Apoio ao RS e decisão do STF sobre ICMS", description: "ANTAQ isenta tarifas para ajuda humanitária ao RS e STF valida ICMS sobre transporte marítimo.", icon: "⚖️" },
    { month: "Junho", title: "Plano de Ação para transição energética nos portos", description: "Parceria entre Brasil e Países Baixos para desenvolver 'Green Ports', com foco em hidrogênio verde.", icon: "🍃" },
    { month: "Julho", title: "Adesão do Brasil a novas normas de segurança da IMO", description: "Decreto aprova emendas à Convenção SOLAS, elevando os padrões de segurança da navegação nacional.", icon: "🛡️" },
    { month: "Agosto", title: "Leilão de cinco terminais portuários em três estados", description: "ANTAQ formaliza novos arrendamentos em Recife, Rio de Janeiro e Rio Grande, com R$ 73,9 milhões em investimentos.", icon: "⚓" },
    { month: "Setembro", title: "Preços portuários mais transparentes", description: "ANTAQ torna obrigatório o envio de dados ao Sistema de Acompanhamento de Preços Portuários (APP).", icon: "📊" },
    { month: "Outubro", title: "Sancionado o marco legal do Combustível do Futuro", description: "Lei nº 14.993/2024 estabelece diretrizes para uso de diesel verde e biometano na navegação.", icon: "⛽" },
    { month: "Novembro", title: "Concessão do canal de Paranaguá avança no TCU", description: "Ministério de Portos e Aeroportos encaminha estudos para licitação do canal de acesso ao Porto de Paranaguá.", icon: "🚢" },
    { month: "Dezembro", title: "Leilão recorde projeta R$ 3,6 bilhões em investimentos", description: "Maior leilão da história do setor portuário concede três terminais em Itaguaí, Maceió e Santana.", icon: "🏆" }
  ]
};

export default function Home() {
  return (
    <div className="min-h-screen bg-white">
      <HeroSection data={waterwayData} />
      
      <StatsSection stats={waterwayData.stats} />
      
      <TimelineSection timeline={waterwayData.timeline} />
      
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Evolução dos Investimentos</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Análise dos investimentos públicos no setor aquaviário brasileiro
            </p>
          </div>
          <InvestmentChart />
        </div>
      </section>

      <CargoCharts />

      <ArcoNorteSection />

      <FleetAndWaterwaysSection />
      
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Acidentes Aquaviários</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Análise de acidentes e vítimas no transporte aquaviário
            </p>
          </div>
          <AccidentChart />
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Infraestrutura Aquaviária</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Distribuição de portos organizados e terminais pelo Brasil
            </p>
          </div>

                <div className="flex justify-center mb-16">
            <picture>
              <source
                media="(min-width: 768px)"
                srcSet={aquaviario}
              />
              <img
                src={aquaviario}
                alt="Mapa da Infraestrutura Aeroportuária no Brasil"
                className="rounded-lg shadow-xl w-full max-w-6xl h-auto border-4 border-white"
                loading="lazy"
                decoding="async"
              />
            </picture>
          </div>

          <div className="mt-16 bg-gradient-to-r from-blue-50 to-sky-50 rounded-xl p-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Panorama da Infraestrutura Portuária</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div className="bg-white/60 p-6 rounded-lg">
                <div className="text-4xl font-bold text-blue-600 mb-2">35</div>
                <p className="text-gray-700 font-semibold mb-2">Portos Organizados</p>
                <p className="text-sm text-gray-600">Principais complexos públicos que estruturam a logística nacional.</p>
              </div>
              <div className="bg-white/60 p-6 rounded-lg">
                <div className="text-4xl font-bold text-green-600 mb-2">175</div>
                <p className="text-gray-700 font-semibold mb-2">Terminais de Uso Privado (TUPs)</p>
                <p className="text-sm text-gray-600">Infraestruturas privadas que respondem por 63% da movimentação de cargas.</p>
              </div>
              <div className="bg-white/60 p-6 rounded-lg">
                <div className="text-4xl font-bold text-purple-600 mb-2">55</div>
                <p className="text-gray-700 font-semibold mb-2">Instalações de Pequeno Porte (IP4s)</p>
                <p className="text-sm text-gray-600">Essenciais para a navegação interior, especialmente na Bacia Amazônica.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Acesse o Diagnóstico Completo
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Baixe o documento completo e tenha acesso a todos os dados, análises detalhadas e 
            insights estratégicos sobre o setor aquaviário brasileiro.
          </p>
    <a
  href={diagnosticoPdf}
  download="diagnostico2024.pdf"
  rel="noopener noreferrer"
>
  <Button
    size="lg"
    className="bg-white text-blue-600 hover:bg-gray-50 px-8 py-4 text-lg group"
  >
    <Download className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
    Download do Diagnóstico
    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
  </Button>
</a>

        </div>
      </section>

      {/* <footer className="py-12 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Anchor className="w-6 h-6" />
                INFRA S.A.
              </h3>
              <p className="text-gray-400 mb-4">
                Observatório Nacional de Transporte e Logística
              </p>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <MapPin className="w-4 h-4" />
                Brasília, DF - Brasil
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Recursos</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Diagnósticos</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Relatórios</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Dados Abertos</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Metodologia</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Contato</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Fale Conosco</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Imprensa</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Parcerias</a></li>
                <li><a href="https://www.infrasa.gov.br" className="hover:text-white transition-colors">Site Oficial</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 INFRA S.A. - Todos os direitos reservados.</p>
          </div>
        </div>
      </footer> */}
    </div>
  );
}